// « Importer un PDF » on an existing album (owner, 2026-10-17 — #1250).
//
// The client's finished PDF goes straight into THIS album: every page becomes
// one print-grade photo and one full-page (1-up) page, in the PDF's order and
// with its design untouched — the same converter as Orders → Nouvelle commande
// (lib/importNativeAlbum convertPdfPagesToPhotos). No JPG export, no page-by-
// page upload. When the album already has pages, the admin chooses: replace
// them (their photos go back to the tray, never deleted) or add after them.
//
// Nothing is sent to the client and the order status does not move. The print
// PDFs are NOT re-rendered here: the page's own « PDF antérieurs » warning
// says so, and the admin regenerates the usual way.
import { useId, useRef, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { sessionsApi, settingsApi, ordersApi } from '@/lib/api';
import { convertPdfPagesToPhotos } from '@/lib/importNativeAlbum';
import { pdfPageCount, pdfPageThumbnail } from '@/lib/pdfRaster';
import { mergeImportedPages, contentPages, type ImportMode } from '@/lib/albumPdfMerge';
import { useT } from '@/lib/i18n';
import { AlbumRegenerateButton } from './AlbumRegenerateButton';
import { cn } from '@/lib/utils';

/** apiFetch's errors carry the HTTP status. */
const statusOf = (e: unknown) => (e as { status?: number } | null)?.status;

const LOCKED = new Set(['ready-to-print', 'in-production', 'printed', 'ready-to-deliver', 'shipped', 'delivered']);

export function AlbumPdfImportControl({ shopifyOrderId, albumIndex, orderStatus }: {
  shopifyOrderId: string;
  albumIndex: number;
  orderStatus: string;
}) {
  const { t } = useT();
  const [open, setOpen] = useState(false);
  const locked = LOCKED.has(orderStatus);
  return (
    <div className="px-3 py-2 border-t border-gray-100 flex items-center gap-2 flex-wrap">
      <AlbumRegenerateButton shopifyOrderId={shopifyOrderId} albumIndex={albumIndex} />
      <button
        type="button"
        onClick={() => setOpen(true)}
        disabled={locked}
        title={locked
          ? t('Album verrouillé (en impression ou livré) — repassez la commande à une étape d’édition pour importer.')
          : t('Importer le PDF du client dans cet album : chaque page devient une page de l’album, dans le même ordre.')}
        className="inline-flex items-center gap-1.5 px-2.5 py-1 text-[11px] font-medium rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50 hover:border-brand-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <span aria-hidden>📥</span>
        {t('Importer un PDF')}
      </button>
      {open && (
        <ImportDialog shopifyOrderId={shopifyOrderId} albumIndex={albumIndex} onClose={() => setOpen(false)} />
      )}
    </div>
  );
}


type Stage = 'pick' | 'ready' | 'working' | 'done' | 'error';
type PickedFile = { name: string; bytes: Uint8Array; pages: number; thumb: string | null };

/** The run's steps, in order — the list under the progress bar ticks them off. */
const STEPS = ['prepare', 'convert', 'save', 'reprice'] as const;
type Step = typeof STEPS[number];

const isPdf = (f: File) => f.type === 'application/pdf' || /\.pdf$/i.test(f.name ?? '');

/** « 1,4 Mo » — never « 0 Mo » for a small file. */
function sizeMo(bytes: number): string {
  const mo = Math.max(0.1, bytes / (1024 * 1024));
  return `${mo.toLocaleString('fr-FR', { maximumFractionDigits: 1 })} Mo`;
}

/** convertPdfPagesToPhotos reports « Conversion page i/N… »: page i is being done, i−1 are finished. */
function parsePageProgress(msg: string): { i: number; n: number } | null {
  const m = /(\d+)\s*\/\s*(\d+)/.exec(msg);
  return m ? { i: Number(m[1]), n: Number(m[2]) } : null;
}

/** Overall %: the page conversion is the long part (5 → 90 %), the rest are short steps. */
function percentOf(step: Step, conv: { i: number; n: number } | null): number {
  if (step === 'prepare') return 3;
  if (step === 'convert') return conv && conv.n > 0 ? Math.round(5 + (85 * (conv.i - 1)) / conv.n) : 5;
  if (step === 'save') return 92;
  return 97;
}

function PdfIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 40 48" fill="none" aria-hidden>
      <path d="M6 2h20l12 12v30a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2Z" fill="#fff" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
      <path d="M26 2v10a2 2 0 0 0 2 2h10" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
      <rect x="0" y="26" width="28" height="13" rx="3" fill="currentColor" />
      <text x="14" y="35.5" textAnchor="middle" fontSize="9" fontWeight="700" fill="#fff" fontFamily="system-ui, sans-serif">PDF</text>
    </svg>
  );
}

function Spinner({ className }: { className?: string }) {
  return <span className={cn('inline-block rounded-full border-2 border-brand-200 border-t-brand-600 animate-spin motion-reduce:animate-none', className)} aria-hidden />;
}

function ImportDialog({ shopifyOrderId, albumIndex, onClose }: { shopifyOrderId: string; albumIndex: number; onClose: () => void }) {
  const { t } = useT();
  const queryClient = useQueryClient();
  const inputId = useId();
  const [stage, setStage] = useState<Stage>('pick');
  const [reading, setReading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [file, setFile] = useState<PickedFile | null>(null);
  const [existing, setExisting] = useState<number | null>(null);
  const [mode, setMode] = useState<ImportMode>('append');
  const [step, setStep] = useState<Step>('prepare');
  const [conv, setConv] = useState<{ i: number; n: number } | null>(null);
  const [progress, setProgress] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<{ added: number; total: number } | null>(null);
  const busy = stage === 'working';
  // Pages already converted and uploaded: « Réessayer » after a failed save does not upload them twice.
  const converted = useRef<string[] | null>(null);

  async function pick(f: File | null | undefined) {
    if (!f || reading) return;
    setError(null);
    if (!isPdf(f)) {
      setError(t('« {name} » n’est pas un PDF. Choisissez un fichier .pdf.', { name: f.name || '?' }));
      return;
    }
    setReading(true);
    try {
      const bytes = new Uint8Array(await f.arrayBuffer());
      const [pages, thumb, sess] = await Promise.all([
        pdfPageCount(bytes),
        pdfPageThumbnail(bytes, 0).catch(() => null),
        sessionsApi.get(shopifyOrderId, albumIndex),
      ]);
      if (!pages) throw new Error(t('Ce PDF ne contient aucune page.'));
      const have = contentPages(sess.session?.pages).length;
      converted.current = null;
      setExisting(have);
      setMode(have > 0 ? 'append' : 'replace');
      setFile({ name: f.name, bytes, pages, thumb });
      setStage('ready');
    } catch (e) {
      setError(t('Fichier illisible : {msg}', { msg: (e as Error)?.message ?? '?' }));
    } finally {
      setReading(false);
    }
  }

  /** « Changer »: back to the drop zone; a new file is converted afresh. */
  function changeFile() {
    converted.current = null;
    setFile(null);
    setError(null);
    setConv(null);
    setStage('pick');
  }

  async function run() {
    if (!file) return;
    setStage('working');
    setError(null);
    setStep('prepare');
    try {
      setProgress(t('Préparation de l’album…'));
      const [{ session }, { settings }] = await Promise.all([
        sessionsApi.get(shopifyOrderId, albumIndex),
        settingsApi.get(),
      ]);
      if (!session?.id) throw new Error(t('Album introuvable.'));
      setStep('convert');
      if (converted.current) setConv({ i: converted.current.length, n: converted.current.length });
      const onConvert = (msg: string) => {
        setProgress(msg);
        const p = parsePageProgress(msg);
        if (p) setConv(p);
      };
      const photoIds = converted.current
        ?? await convertPdfPagesToPhotos(session.id, file.bytes, settings?.pdf_bleed_mm ?? 3, onConvert);
      converted.current = photoIds;
      setConv({ i: photoIds.length, n: photoIds.length });
      setStep('save');
      setProgress(t('Enregistrement des pages…'));
      // The album may have been saved while the pages converted (an open
      // editor): read it again and merge into THAT, once, rather than
      // overwrite it (the session guard answers 409 stale_write).
      const save = async (s: typeof session) => {
        const merged = mergeImportedPages(s.pages, photoIds, mode);
        await sessionsApi.upsert(shopifyOrderId, albumIndex, {
          pages: merged.pages, page_count: merged.pageCount,
          ...(s.last_saved_at ? { expected_last_saved_at: s.last_saved_at } : {}),
        });
        return merged.pageCount;
      };
      let total: number;
      try {
        total = await save(session);
      } catch (e) {
        if (!(statusOf(e) === 409)) throw e;
        const again = (await sessionsApi.get(shopifyOrderId, albumIndex)).session;
        if (!again) throw e;
        total = await save(again);
      }
      setStep('reprice');
      setProgress(t('Recalcul des pages et du prix…'));
      try { await ordersApi.importReprice(shopifyOrderId); } catch { /* the pages are saved; pricing follows on the next quote */ }
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['order', shopifyOrderId] }),
        queryClient.invalidateQueries({ queryKey: ['album-session', shopifyOrderId, albumIndex] }),
        queryClient.invalidateQueries({ queryKey: ['admin-order-follow-up', shopifyOrderId] }),
      ]);
      setResult({ added: photoIds.length, total });
      setStage('done');
    } catch (e) {
      const msg = statusOf(e) === 423
        ? t('Album verrouillé (en impression ou livré).')
        : (e as Error)?.message ?? '?';
      setError(t('Import interrompu : {msg}', { msg }));
      setStage('error');
    } finally {
      setProgress('');
    }
  }

  const pct = stage === 'done' ? 100 : percentOf(step, conv);
  const stepIdx = STEPS.indexOf(step);
  const stepLabel: Record<Step, string> = {
    prepare: t('Préparation de l’album'),
    convert: conv ? t('Conversion des pages · {i}/{n}', { i: Math.min(conv.i, conv.n), n: conv.n }) : t('Conversion des pages'),
    save: t('Enregistrement des pages'),
    reprice: t('Recalcul des pages et du prix'),
  };
  const actionLabel = existing && existing > 0
    ? (mode === 'replace' ? t('Remplacer par le PDF') : t('Ajouter les pages du PDF'))
    : t('Importer les pages');

  return (
    <Modal isOpen onClose={busy ? () => {} : onClose} title={t('Importer un PDF — album {n}', { n: albumIndex })} size="md">
      <div className="space-y-4 text-sm p-4 sm:p-5">
        {stage === 'pick' && (
          <>
            <p className="text-gray-600 text-[13px] leading-relaxed">
              {t('Chaque page du PDF devient une page de l’album, dans le même ordre et avec son design.')}
            </p>
            <label
              htmlFor={inputId}
              onDragEnter={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'copy'; if (!dragOver) setDragOver(true); }}
              onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setDragOver(false); }}
              onDrop={(e) => { e.preventDefault(); setDragOver(false); void pick(e.dataTransfer.files?.[0]); }}
              data-testid="pdf-dropzone"
              aria-busy={reading || undefined}
              className={cn(
                'relative flex flex-col items-center justify-center gap-3 text-center rounded-2xl border-2 border-dashed px-4 py-8 sm:py-10 transition-colors cursor-pointer select-none',
                'has-[input:focus-visible]:ring-2 has-[input:focus-visible]:ring-brand-500 has-[input:focus-visible]:ring-offset-2',
                dragOver
                  ? 'border-brand-500 bg-brand-50'
                  : 'border-gray-300 bg-gray-50/60 hover:border-brand-400 hover:bg-brand-50/50',
                reading && 'cursor-wait',
              )}
            >
              <input
                id={inputId}
                type="file"
                accept="application/pdf,.pdf"
                disabled={reading}
                onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; void pick(f); }}
                className="sr-only"
              />
              {reading ? (
                <>
                  <Spinner className="w-9 h-9 border-[3px]" />
                  <span className="font-medium text-gray-800" role="status">{t('Lecture du PDF…')}</span>
                </>
              ) : (
                <>
                  <span className={cn('transition-transform', dragOver && 'motion-safe:-translate-y-1 motion-safe:scale-105')}>
                    <PdfIcon className="w-12 h-14 text-brand-600" />
                  </span>
                  <span className="space-y-1">
                    <span className="block font-medium text-gray-900">
                      {dragOver ? t('Déposez le PDF') : t('Glissez le PDF ici ou cliquez pour choisir')}
                    </span>
                    <span className="block text-xs text-gray-500">{t('Fichier PDF uniquement · une page du PDF = une page de l’album')}</span>
                  </span>
                  <span className="inline-flex items-center px-3 py-1.5 rounded-lg bg-white border border-gray-200 text-xs font-medium text-gray-700 shadow-sm">
                    {t('Choisir un fichier')}
                  </span>
                </>
              )}
            </label>
          </>
        )}

        {file && (stage === 'ready' || stage === 'error') && (
          <div className="flex gap-3 items-center rounded-xl border border-gray-200 bg-white p-3">
            <div className="shrink-0 w-12 aspect-[200/297] rounded-md overflow-hidden border border-gray-200 bg-gray-50 flex items-center justify-center">
              {file.thumb ? <img src={file.thumb} alt="" className="w-full h-full object-cover" /> : <PdfIcon className="w-7 h-8 text-brand-600" />}
            </div>
            <div className="min-w-0 flex-1">
              <p className="font-medium text-gray-900 truncate" title={file.name}>{file.name}</p>
              <p className="text-xs text-gray-500">
                {t('{n} pages dans le PDF', { n: file.pages })} · <span className="whitespace-nowrap">{sizeMo(file.bytes.byteLength)}</span>
              </p>
            </div>
            <button
              type="button"
              onClick={changeFile}
              className="shrink-0 text-xs font-medium text-brand-700 hover:text-brand-800 underline underline-offset-2 rounded px-1.5 py-1 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
            >
              {t('Changer')}
            </button>
          </div>
        )}

        {stage === 'ready' && file && (
          existing && existing > 0 ? (
            <fieldset className="space-y-2">
              <legend className="text-xs font-semibold text-gray-700 mb-2">
                {t('L’album a déjà {n} pages. Que faire ?', { n: existing })}
              </legend>
              <div className="grid gap-2 sm:grid-cols-2">
                <ModeCard
                  checked={mode === 'append'}
                  onSelect={() => setMode('append')}
                  title={t('Ajouter à la fin')}
                  detail={t('{a} pages gardées + {b} pages du PDF = {c} pages.', { a: existing, b: file.pages, c: existing + file.pages })}
                  icon={<path d="M4 5h9v14H4zM16 9v6M13 12h6" />}
                />
                <ModeCard
                  checked={mode === 'replace'}
                  onSelect={() => setMode('replace')}
                  title={t('Remplacer les pages')}
                  detail={t('L’album devient les {b} pages du PDF. Les photos actuelles retournent dans le bac de l’éditeur — rien n’est supprimé.', { b: file.pages })}
                  icon={<path d="M4 8h12l-3-3M20 16H8l3 3" />}
                />
              </div>
            </fieldset>
          ) : (
            <p className="text-xs text-gray-600 rounded-lg bg-gray-50 px-3 py-2">{t('L’album est vide : il aura les {n} pages du PDF.', { n: file.pages })}</p>
          )
        )}

        {stage === 'working' && file && (
          <div className="space-y-4">
            <div className="flex items-center gap-4">
              <div className="relative shrink-0 w-16 h-[95px]" aria-hidden>
                {/* the album stack the pages slide into */}
                <span className="absolute inset-0 rounded-md bg-brand-100 border border-brand-200 translate-x-2 translate-y-1.5 rotate-3" />
                <span className="absolute inset-0 rounded-md bg-brand-50 border border-brand-200 translate-x-1 translate-y-0.5 rotate-1" />
                <span className="absolute inset-0 rounded-md overflow-hidden border border-gray-200 bg-white shadow-sm flex items-center justify-center">
                  {file.thumb ? <img src={file.thumb} alt="" className="w-full h-full object-cover" /> : <PdfIcon className="w-8 h-9 text-brand-600" />}
                  <span className="pdfimp-shimmer" />
                </span>
                {step === 'convert' && <span className="pdfimp-sheet absolute inset-2 rounded-sm bg-white border border-brand-300 shadow" />}
              </div>
              <div className="min-w-0 flex-1">
                <p className="font-heading text-3xl leading-none text-gray-900 tabular-nums">{pct}<span className="text-lg text-gray-500"> %</span></p>
                <p className="mt-1.5 text-xs text-gray-600 truncate" role="status" aria-live="polite">{progress || stepLabel[step]}</p>
              </div>
            </div>
            <div
              className="h-2 rounded-full bg-gray-100 overflow-hidden"
              role="progressbar"
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={pct}
              aria-label={t('Progression de l’import')}
            >
              <div className="h-full rounded-full bg-brand-500 transition-[width] duration-500 ease-out motion-reduce:transition-none" style={{ width: `${pct}%` }} />
            </div>
            <StepList steps={STEPS.map((s, i) => ({ key: s, label: stepLabel[s], state: i < stepIdx ? 'done' : i === stepIdx ? 'active' : 'todo' }))} />
            <p className="text-[11px] text-gray-500 flex items-center gap-1.5">
              <svg className="w-3.5 h-3.5 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden><circle cx="12" cy="12" r="9" /><path d="M12 8v5M12 16h.01" strokeLinecap="round" /></svg>
              {t('Gardez cette fenêtre ouverte jusqu’à la fin.')}
            </p>
          </div>
        )}

        {stage === 'error' && file && (
          <StepList steps={STEPS.map((s, i) => ({ key: s, label: stepLabel[s], state: i < stepIdx ? 'done' : i === stepIdx ? 'failed' : 'todo' }))} />
        )}

        {stage === 'done' && result && (
          <div className="text-center space-y-4 pt-1">
            <div className="pdfimp-pop mx-auto w-14 h-14 rounded-full bg-emerald-50 border border-emerald-200 flex items-center justify-center">
              <svg className="w-7 h-7 text-emerald-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                <path className="pdfimp-tick" d="M5 12.5l4.5 4.5L19 7.5" />
              </svg>
            </div>
            <div role="status">
              <h3 className="font-heading text-xl text-gray-900">{t('Import terminé')}</h3>
              <p className="sr-only">{t('{a} pages importées, l’album a {b} pages.', { a: result.added, b: result.total })}</p>
            </div>
            <div className="grid grid-cols-2 gap-2 text-left">
              <div className="rounded-xl border border-gray-200 px-3 py-2.5">
                <p className="font-heading text-2xl leading-none tabular-nums text-gray-900">{result.added}</p>
                <p className="mt-1 text-xs text-gray-500">{t('pages importées')}</p>
              </div>
              <div className="rounded-xl border border-brand-200 bg-brand-50/60 px-3 py-2.5">
                <p className="font-heading text-2xl leading-none tabular-nums text-gray-900">{result.total}</p>
                <p className="mt-1 text-xs text-gray-500">{t('pages dans l’album')}</p>
              </div>
            </div>
            <p className="text-xs text-gray-600 text-left rounded-lg bg-amber-50 border border-amber-200 px-3 py-2">
              {t('Les PDF d’impression et l’aperçu 3D ne sont pas encore refaits : ouvrez l’éditeur pour vérifier, puis régénérez comme d’habitude.')}
            </p>
          </div>
        )}

        {error && (
          <p className="text-xs text-red-700 rounded-lg bg-red-50 border border-red-200 px-3 py-2" role="alert">{error}</p>
        )}

        <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 pt-1">
          {stage === 'done' ? (
            <>
              <Button variant="ghost" onClick={onClose} className="w-full sm:w-auto">{t('Fermer')}</Button>
              <Link
                to={`/editor/${shopifyOrderId}/${albumIndex}/edit`}
                className="inline-flex items-center justify-center gap-2 min-h-11 md:min-h-0 px-4 py-2 rounded-lg text-sm font-medium text-white bg-brand-500 hover:bg-brand-600 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 w-full sm:w-auto"
              >
                {t('Ouvrir l’éditeur')}
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden><path d="M5 12h14M13 6l6 6-6 6" /></svg>
              </Link>
            </>
          ) : (
            <>
              <Button variant="ghost" onClick={onClose} disabled={busy} className="w-full sm:w-auto">{t('Annuler')}</Button>
              {(stage === 'ready' || stage === 'working') && (
                <Button onClick={run} loading={busy} disabled={busy} className="w-full sm:w-auto">
                  {busy ? t('Import en cours…') : actionLabel}
                </Button>
              )}
              {stage === 'error' && <Button onClick={run} className="w-full sm:w-auto">{t('Réessayer')}</Button>}
            </>
          )}
        </div>
      </div>
    </Modal>
  );
}

/** Replace-or-add as two selectable cards; the real radio stays (sr-only) for keyboard + screen readers. */
function ModeCard({ checked, onSelect, title, detail, icon }: {
  checked: boolean; onSelect: () => void; title: string; detail: string; icon: ReactNode;
}) {
  return (
    <label
      className={cn(
        'relative flex gap-2.5 items-start rounded-xl border-2 p-3 cursor-pointer transition-colors',
        'has-[input:focus-visible]:ring-2 has-[input:focus-visible]:ring-brand-500 has-[input:focus-visible]:ring-offset-1',
        checked ? 'border-brand-500 bg-brand-50' : 'border-gray-200 bg-white hover:border-brand-300',
      )}
    >
      <input type="radio" name="import-mode" checked={checked} onChange={onSelect} className="sr-only" />
      <span className={cn('shrink-0 w-8 h-8 rounded-lg flex items-center justify-center', checked ? 'bg-brand-500 text-white' : 'bg-gray-100 text-gray-500')}>
        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>{icon}</svg>
      </span>
      <span className="min-w-0 pr-5">
        <span className="block font-medium text-gray-900">{title}</span>
        <span className="block mt-0.5 text-xs text-gray-500 leading-snug">{detail}</span>
      </span>
      <span
        className={cn('absolute top-3 right-3 w-4 h-4 rounded-full border-2 flex items-center justify-center', checked ? 'border-brand-500' : 'border-gray-300')}
        aria-hidden
      >
        {checked && <span className="w-2 h-2 rounded-full bg-brand-500" />}
      </span>
    </label>
  );
}

type StepState = 'done' | 'active' | 'failed' | 'todo';
function StepList({ steps }: { steps: Array<{ key: string; label: string; state: StepState }> }) {
  return (
    <ol className="space-y-1.5">
      {steps.map((s) => (
        <li key={s.key} className="flex items-center gap-2 text-xs" data-step={s.key} data-state={s.state}>
          <span className="shrink-0 w-4 h-4 flex items-center justify-center" aria-hidden>
            {s.state === 'done' ? (
              <span className="w-4 h-4 rounded-full bg-emerald-500 text-white flex items-center justify-center">
                <svg className="w-2.5 h-2.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12.5l4.5 4.5L19 7.5" /></svg>
              </span>
            ) : s.state === 'active' ? (
              <Spinner className="w-4 h-4" />
            ) : s.state === 'failed' ? (
              <span className="w-4 h-4 rounded-full bg-red-500 text-white flex items-center justify-center">
                <svg className="w-2.5 h-2.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="4" strokeLinecap="round"><path d="M7 7l10 10M17 7L7 17" /></svg>
              </span>
            ) : (
              <span className="w-2 h-2 rounded-full bg-gray-300" />
            )}
          </span>
          <span className={cn(
            s.state === 'done' && 'text-gray-500',
            s.state === 'active' && 'text-gray-900 font-medium',
            s.state === 'failed' && 'text-red-700 font-medium',
            s.state === 'todo' && 'text-gray-400',
          )}>{s.label}</span>
        </li>
      ))}
    </ol>
  );
}
